import { useMutation } from '@apollo/client';
import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { useDispatch, useSelector } from 'react-redux';
import ApplePayCheckout from '../../components/ApplePayCheckout/ApplePayCheckout';
import ErrorMessage, { useError } from '../../components/ErrorMessage/ErrorMessage';
import Loading from '../../components/Loading/Loading';
import WebViewAppBar from '../../components/WebViewAppBar/WebViewAppBar';
import HideChatWidget from '../../components/ZendeskChat/HideChatWidget';
import { useCartPricing } from '../../contexts/CartPricingContext';
import { billingAccountQuery, updateBillingInfoMutation } from '../../graphql-operations';
import { osSupportsApplePay } from '../../hooks/useShouldShowApplePay';
import * as events from '../../lib/analytics/events';
import { isForModuleCartItem } from '../../lib/cart';
import { useSubscriptionCartMode } from '../../lib/cartModes';
import { ErrorMessages, getCustomerMessageFromApolloError, logInternalError } from '../../lib/errors';
import { formattedCentsToDollars } from '../../lib/util';
import { cartActions } from '../../reducers/subscriptionCart';
import * as types from '../../types';
import { gqlTypes } from '../../types';
import { UpdateBillingInfoFunction } from '../Checkout/pages/Billing/BillingInfo';
import EditCreditCardBillingInfo from '../Checkout/pages/Billing/EditCreditCardBillingInfo';
import { usePurchaseSubscriptionContext } from '../PurchaseSubscription/context/PurchaseSubscriptionContext';
import styles from './PrepaidActivation.module.scss';
import PrepaidSubscriptionInfo from './PrepaidSubscriptionInfo';
import useActivateSubscription from './useActivateSubscription';
import usePrepaidSubscriptionOption from './usePrepaidSubscriptionOption';
import { HideCookieConsentBanner } from '../../lib/oneTrust';
import { FiCountry, getCurrencyForCountry, getFiCountryFromString, useLocale } from '../../contexts/LocaleContext';

interface CreditCardFormProps {
  activateSubscriptionLoading: boolean;
  activateSubscription: () => Promise<void>;
  onSuccess: () => void;
  error?: string;
  errorID?: string;
  setError: (message: string) => void;
  clearError: () => void;
  renewalMonths: number;
  renewalFormattedPrice: string;
  nextBillingDate: string;
  onBillingCountryChange?: (country: string) => void;
}

const FORMATTED_ORDER_SOURCE: Record<gqlTypes.OrderSource, string> = {
  [gqlTypes.OrderSource.AMAZON]: 'Amazon',
  [gqlTypes.OrderSource.CHEWY]: 'Chewy',
  [gqlTypes.OrderSource.COSTCO]: 'Costco',
  [gqlTypes.OrderSource.COSTCO_ROADSHOW]: 'Costco',
  [gqlTypes.OrderSource.FLIP]: 'Flip',
  [gqlTypes.OrderSource.HSN]: 'HSN',
  [gqlTypes.OrderSource.MANUAL]: '', // Legacy order source
  [gqlTypes.OrderSource.PETSMART]: 'PetSmart',
  [gqlTypes.OrderSource.PETWATCH]: '24PetWatch',
  [gqlTypes.OrderSource.RACHIO]: 'Rachio',
  [gqlTypes.OrderSource.RETAIL]: 'Retail',
  [gqlTypes.OrderSource.RECURLY]: 'Fi',
  [gqlTypes.OrderSource.RESCUE]: 'Rescue',
  [gqlTypes.OrderSource.STUNTPUPPY]: 'Stunt Puppy',
  [gqlTypes.OrderSource.INTERNAL]: 'Internal',
  [gqlTypes.OrderSource.PET_RESORTS]: 'Pet Resorts',
};

function subtextForOrderSource(orderSource: gqlTypes.OrderSource, t: TFunction) {
  const formattedOrderSource = FORMATTED_ORDER_SOURCE[orderSource];

  switch (orderSource) {
    case gqlTypes.OrderSource.RECURLY:
      return t('included_in_your_gift');
    default:
      return t('prepaid_with_your_order_source_purchase', { orderSource: formattedOrderSource });
  }
}

export function CreditCardForm({
  activateSubscriptionLoading,
  activateSubscription,
  onSuccess,
  error,
  errorID,
  setError,
  clearError,
  renewalMonths,
  renewalFormattedPrice,
  nextBillingDate,
  onBillingCountryChange,
}: CreditCardFormProps) {
  const { t } = useTranslation();
  const showApplePay = useSelector(
    (state: types.AppState) => !!(state.config.siteConfig.showApplePay && osSupportsApplePay()),
  );

  const [mutation, { loading: updateBillingMutationLoading }] = useMutation<
    gqlTypes.updateBillingInfo,
    gqlTypes.updateBillingInfoVariables
  >(updateBillingInfoMutation);

  const handleUpdateBillingInfo: UpdateBillingInfoFunction = useCallback(
    async ({ input, recaptchaToken }) => {
      if (updateBillingMutationLoading || activateSubscriptionLoading) {
        return;
      }

      events.prepaidActivation.submitCreditCard();

      // Update the billing info first
      try {
        await mutation({
          variables: {
            input,
            recaptchaToken,
          },
          refetchQueries: [{ query: billingAccountQuery }],
        });
      } catch (err) {
        const customerMessage = getCustomerMessageFromApolloError(err);
        if (customerMessage) {
          setError(customerMessage);
        } else {
          logInternalError(err, {
            fingerprint: ['{{ default }}', err.message],
            tags: {
              checkoutStep: 'updateBillingInfo',
              context: 'giftFiActivation',
              paymentMethod: 'creditCard',
            },
          });

          setError(ErrorMessages.DEFAULT);
        }

        return;
      }

      // Then create the subscription
      try {
        await activateSubscription();
      } catch (err) {
        const customerMessage = getCustomerMessageFromApolloError(err);
        if (customerMessage) {
          console.log('customerMessage', customerMessage);
          setError(customerMessage);
        } else {
          logInternalError(err, {
            fingerprint: ['{{ default }}', err.message],
            tags: {
              checkoutStep: 'activateSubscription',
              context: 'giftFiActivation',
              paymentMethod: 'creditCard',
            },
          });

          setError(ErrorMessages.DEFAULT);
        }
      }
    },
    [updateBillingMutationLoading, activateSubscriptionLoading, mutation, activateSubscription, setError],
  );

  return (
    <>
      <EditCreditCardBillingInfo
        actionText={t('save')}
        updateBillingInfo={handleUpdateBillingInfo}
        billingInfo={null}
        submitting={updateBillingMutationLoading || activateSubscriptionLoading}
        setError={setError}
        clearError={clearError}
        compactForm
        onBillingCountryChange={onBillingCountryChange}
        actionNote={
          <>
            {error && <ErrorMessage errors={[error]} errorID={errorID} />}
            <div className={styles.renewalNotice}>
              {t('your_membership_will_renew_on_date_for_months_months_price_you_can_change_this_in_your_fi_account', {
                date: nextBillingDate,
                months: renewalMonths,
                price: renewalFormattedPrice,
              })}
            </div>
          </>
        }
        additionalActions={
          <>
            {showApplePay && (
              <ApplePayCheckout
                errorAlertOnAccountExists
                events={events.prepaidActivation}
                onError={(err) => setError(err.message)}
                onSuccess={onSuccess}
                showNonSafariApplePayButton={false}
              />
            )}
          </>
        }
      />
    </>
  );
}

export default function PrepaidActivationCreditCard() {
  const { t } = useTranslation();
  const { cartSelector } = useSubscriptionCartMode();
  const { device } = usePurchaseSubscriptionContext();
  const { error, errorID, setError, clearError } = useError();
  const cart = useSelector(cartSelector);
  const dispatch = useDispatch();
  const prepaidSubscriptionInfo = usePrepaidSubscriptionOption();
  const cartPricing = useCartPricing();
  const { currency, country: localeCountry } = useLocale();

  // Billing country is defaulted to the locale country on the form
  const [billingCountry, setBillingCountry] = useState(localeCountry);

  const handleBillingCountryChange = useCallback(
    (country: string) => {
      const fiCountry = getFiCountryFromString(country) ?? FiCountry.US;
      const newCurrency = getCurrencyForCountry(fiCountry);
      dispatch(cartActions.setCurrency(newCurrency));
      setBillingCountry(fiCountry);
    },
    [dispatch],
  );

  const {
    activateSubscription,
    loading: activateSubscriptionLoading,
    onSuccess,
  } = useActivateSubscription({
    setError,
  });

  // Remove any coupon codes and set the correct cart item for prepaid activation
  useEffect(() => {
    if (cart.couponCode) {
      dispatch(cartActions.removeCoupon());
    }

    if (!prepaidSubscriptionInfo) {
      return;
    }

    // If there's already an item in this cart from browser local storage, be sure to overwrite it with the correct
    // module id and sku
    const cartItems = Object.values(cart.cartItems);
    if (
      !cartItems.length ||
      cartItems[0].lineItem.sku !== prepaidSubscriptionInfo.subscriptionOptionSku ||
      (isForModuleCartItem(cartItems[0]) && cartItems[0].forModuleId !== device.moduleId)
    ) {
      dispatch(
        cartActions.setCartItem({
          moduleId: device.moduleId,
          sku: prepaidSubscriptionInfo.subscriptionOptionSku,
          currency,
        }),
      );
    }
  }, [cart.cartItems, cart.couponCode, device.moduleId, dispatch, prepaidSubscriptionInfo, currency]);

  // Sync cart currency from billing country (ensures price displays correctly before user interacts with billing
  // country dropdown)
  useEffect(() => {
    const billingCountryCurrency = getCurrencyForCountry(billingCountry);
    if (billingCountryCurrency !== cart.currency) {
      dispatch(cartActions.setCurrency(billingCountryCurrency));
    }
  }, [billingCountry, cart.currency, dispatch]);

  if (!prepaidSubscriptionInfo) {
    return (
      <div className={styles.container}>
        <WebViewAppBar />
        <ErrorMessage
          errors={[
            t(
              'we_were_unable_to_find_the_prepaid_membership_for_this_collar_or_your_prepaid_membership_has_already_been_activated_please_contact_support',
            ),
          ]}
        />
      </div>
    );
  }

  if (!Object.keys(cart.cartItems).length) {
    return <Loading />;
  }

  const { subscriptionOptionName, formattedPrice, renewalSubscriptionOption, renewalFormattedPrice, nextBillingDate } =
    prepaidSubscriptionInfo;

  const formattedDueToday = formattedCentsToDollars(cartPricing.totalInCents);

  return (
    <div className={styles.container}>
      <WebViewAppBar title={t('complete_activation')} />
      <div className={styles.content}>
        <div className={styles.section}>
          <PrepaidSubscriptionInfo
            subscriptionOptionName={subscriptionOptionName}
            price={formattedPrice}
            subtext={subtextForOrderSource(prepaidSubscriptionInfo.orderSource, t)}
            dueToday={formattedDueToday}
          />
        </div>
        <div className={classNames(styles.section, styles.billing)}>
          <CreditCardForm
            activateSubscriptionLoading={activateSubscriptionLoading}
            activateSubscription={activateSubscription}
            onSuccess={onSuccess}
            error={error}
            errorID={errorID}
            setError={setError}
            clearError={clearError}
            renewalMonths={renewalSubscriptionOption.renewalMonths}
            renewalFormattedPrice={renewalFormattedPrice}
            nextBillingDate={nextBillingDate}
            onBillingCountryChange={handleBillingCountryChange}
          />
        </div>
      </div>
      <HideChatWidget />
      <HideCookieConsentBanner />
    </div>
  );
}
